import { applicationIdentityKey } from '../councils';

type SearchChipLike = unknown[] | null | undefined;
type ApplicationScalar = string | number | null | undefined;
type ApplicationReferenceLike = {
  application_uid?: ApplicationScalar;
  record_key?: ApplicationScalar;
  source_council?: ApplicationScalar;
  reference_no?: ApplicationScalar;
  ref?: ApplicationScalar;
  reference?: ApplicationScalar;
  id?: ApplicationScalar;
  application_id?: ApplicationScalar;
  group_id?: ApplicationScalar;
  source_record_only?: boolean;
  address?: ApplicationScalar;
  location?: ApplicationScalar;
  latitude?: ApplicationScalar;
  lat?: ApplicationScalar;
  longitude?: ApplicationScalar;
  lng?: ApplicationScalar;
  created_at?: ApplicationScalar;
  scraped_date?: ApplicationScalar;
  validated_date?: ApplicationScalar;
  received_date?: ApplicationScalar;
} | null | undefined;

interface ActivePlanningSearchParams {
  activeChips?: SearchChipLike;
  searchInput?: string | null;
}

export const countActivePlanningSearchSignals = ({
  activeChips,
  searchInput,
}: ActivePlanningSearchParams): number => {
  const chipCount = Array.isArray(activeChips) ? activeChips.length : 0;
  const rawSearchCount = String(searchInput || '').trim() ? 1 : 0;
  return chipCount + rawSearchCount;
};

interface SelectApplicationsParams<T extends ApplicationReferenceLike> {
  searchActive: boolean;
  searchResults: T[];
  fallbackApplications: T[];
  allowSearchResultsOutsideFallback?: boolean;
}

interface GroupedApplicationLike<TApplication extends ApplicationReferenceLike> {
  apps?: TApplication[];
}

type SearchGroupedApplication<TApplication extends ApplicationReferenceLike> = GroupedApplicationLike<TApplication> & {
  key: string;
  baseAddress: string;
  label: string;
  apps: TApplication[];
  lat?: ApplicationScalar;
  lng?: ApplicationScalar;
};

interface SelectGroupedApplicationsParams<TApplication extends ApplicationReferenceLike, TGroup extends GroupedApplicationLike<TApplication>> {
  searchActive: boolean;
  searchResults: TApplication[];
  fallbackGroups: TGroup[];
}

const getApplicationReference = (application: ApplicationReferenceLike): string => {
  if (!application || typeof application !== 'object') {
    return '';
  }

  const reference = application.reference_no
    ?? application.ref
    ?? application.reference
    ?? application.id
    ?? application.application_id;

  return reference == null ? '' : String(reference).trim();
};

const getApplicationIdentity = (application: ApplicationReferenceLike): string => {
  if (!application || typeof application !== 'object') {
    return '';
  }

  return applicationIdentityKey(application as Record<string, unknown>);
};

const getApplicationAddress = (application: ApplicationReferenceLike): string => {
  if (!application || typeof application !== 'object') {
    return '';
  }

  const address = application.address ?? application.location;
  return address == null ? '' : String(address).trim();
};

const normalizeGroupingAddress = (value = ''): string => value
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^(flat|apartment|apt|unit)\s+\d+[a-z]*,?\s*/i, '')
  .replace(/^\d+[a-z]*[f/-]\d+[a-z]*,?\s*/i, '')
  .replace(/^pf\d+\s*,?\s*/i, '');

const getApplicationDateTimestamp = (application: ApplicationReferenceLike): number => {
  if (!application || typeof application !== 'object') {
    return 0;
  }

  const rawDate = application.created_at
    ?? application.scraped_date
    ?? application.validated_date
    ?? application.received_date
    ?? null;

  const timestamp = rawDate == null ? NaN : new Date(String(rawDate)).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortApplicationsNewestFirst = <TApplication extends ApplicationReferenceLike>(
  applications: TApplication[]
): TApplication[] => applications.sort((left, right) => (
  getApplicationDateTimestamp(right) - getApplicationDateTimestamp(left)
));

const buildSearchGroupedApplications = <TApplication extends ApplicationReferenceLike>(
  sourceApplications: TApplication[]
): Array<SearchGroupedApplication<TApplication>> => {
  if (!Array.isArray(sourceApplications) || sourceApplications.length === 0) {
    return [];
  }

  const groupsById = new Map<string, TApplication[]>();
  const addressGroups = new Map<string, TApplication[]>();

  sourceApplications.forEach((application, index) => {
    if (!application || typeof application !== 'object') {
      return;
    }
    if (application.source_record_only) {
      groupsById.set(`source:${applicationIdentityKey(application)}`, [application]);
      return;
    }

    const groupId = application.group_id == null ? '' : String(application.group_id).trim();
    if (groupId) {
      if (!groupsById.has(groupId)) {
        groupsById.set(groupId, []);
      }
      groupsById.get(groupId)?.push(application);
      return;
    }

    const addressKey = normalizeGroupingAddress(getApplicationAddress(application));
    const reference = getApplicationReference(application);
    const fallbackKey = reference ? `ref:${reference}` : `idx:${index}`;
    const key = addressKey || fallbackKey;
    if (!addressGroups.has(key)) {
      addressGroups.set(key, []);
    }
    addressGroups.get(key)?.push(application);
  });

  const groups: Array<SearchGroupedApplication<TApplication>> = [];
  const groupsByAddress = new Map<string, SearchGroupedApplication<TApplication>>();

  const createGroup = (key: string, applications: TApplication[]): SearchGroupedApplication<TApplication> | null => {
    const sortedApps = sortApplicationsNewestFirst([...applications]);
    const firstApplication = sortedApps[0];
    if (!firstApplication || typeof firstApplication !== 'object') {
      return null;
    }

    const baseAddress = getApplicationAddress(firstApplication) || 'Unknown Address';
    return {
      key,
      baseAddress,
      label: sortedApps.length > 1 ? `${baseAddress} (${sortedApps.length} related)` : baseAddress,
      apps: sortedApps,
      lat: firstApplication.latitude ?? firstApplication.lat,
      lng: firstApplication.longitude ?? firstApplication.lng,
    };
  };

  groupsById.forEach((applications, id) => {
    const group = createGroup(`gid:${id}`, applications);
    if (group) {
      groups.push(group);
      const normalizedAddress = normalizeGroupingAddress(group.baseAddress);
      if (!group.apps[0]?.source_record_only && normalizedAddress && !groupsByAddress.has(normalizedAddress)) {
        groupsByAddress.set(normalizedAddress, group);
      }
    }
  });

  addressGroups.forEach((applications, addressKey) => {
    const canMergeByAddress = !addressKey.startsWith('ref:') && !addressKey.startsWith('idx:');
    const target = canMergeByAddress ? groupsByAddress.get(addressKey) : null;

    if (target) {
      target.apps = sortApplicationsNewestFirst([...target.apps, ...applications]);
      target.label = target.apps.length > 1
        ? `${target.baseAddress} (${target.apps.length} related)`
        : target.baseAddress;
      return;
    }

    const group = createGroup(`addr:${addressKey}`, applications);
    if (group) {
      groups.push(group);
      if (!groupsByAddress.has(addressKey)) {
        groupsByAddress.set(addressKey, group);
      }
    }
  });

  return groups.sort((left, right) => (
    getApplicationDateTimestamp(right.apps[0]) - getApplicationDateTimestamp(left.apps[0])
  ));
};

export const hasActivePlanningSearch = ({
  activeChips,
  searchInput,
}: ActivePlanningSearchParams): boolean => (
  countActivePlanningSearchSignals({
    activeChips,
    searchInput,
  }) > 0
);

export const selectPlanningDashboardApplications = <T extends ApplicationReferenceLike>({
  searchActive,
  searchResults,
  fallbackApplications,
  allowSearchResultsOutsideFallback = false,
}: SelectApplicationsParams<T>): T[] => {
  const normalizedFallbackApplications = Array.isArray(fallbackApplications) ? fallbackApplications : [];

  if (!searchActive) {
    return normalizedFallbackApplications;
  }

  const normalizedSearchResults = Array.isArray(searchResults) ? searchResults : [];

  if (allowSearchResultsOutsideFallback) {
    return normalizedSearchResults;
  }

  if (normalizedSearchResults.length === 0 || normalizedFallbackApplications.length === 0) {
    return [];
  }

  const fallbackByIdentity = new Map<string, T>();
  normalizedFallbackApplications.forEach((application) => {
    const identity = getApplicationIdentity(application);
    if (identity) {
      fallbackByIdentity.set(identity, application);
    }
  });

  if (fallbackByIdentity.size === 0) {
    return normalizedSearchResults.filter((application) => (
      normalizedFallbackApplications.includes(application)
    ));
  }

  return normalizedSearchResults.reduce<T[]>((matches, application) => {
    const identity = getApplicationIdentity(application);
    const fallbackMatch = identity ? fallbackByIdentity.get(identity) : null;
    if (fallbackMatch) {
      matches.push(fallbackMatch);
    }
    return matches;
  }, []);
};

export const selectPlanningGroupedApplications = <
  TApplication extends ApplicationReferenceLike,
  TGroup extends GroupedApplicationLike<TApplication>
>({
  searchActive,
  searchResults,
  fallbackGroups,
}: SelectGroupedApplicationsParams<TApplication, TGroup>): TGroup[] => {
  if (!searchActive) {
    return fallbackGroups;
  }

  return buildSearchGroupedApplications(Array.isArray(searchResults) ? searchResults : []) as unknown as TGroup[];
};
